/**
 * Maps this shell's own Firebase Hosting hostname to the legacy Service
 * Portal application it should embed.
 *
 * Firebase Hosting cannot proxy paths into the legacy app's own
 * infrastructure, so each shell hostname is paired 1:1 with a legacy
 * origin here instead of relying on a shared/relative path.
 */
const LEGACY_PORTAL_URL_BY_HOSTNAME: Record<string, string> = {
  'eu.lab.service-portal.tomra.cloud': 'https://devtest.tomraconnect.com',
  'verilab.service-portal.tomra.cloud': 'https://verilab.tomraconnect.com',
  'eu.test.service-portal.tomra.cloud': 'https://www1-test.tomraconnect.com',
  'us.test.service-portal.tomra.cloud': 'https://www3-test.tomraconnect.com',
  'au.test.service-portal.tomra.cloud': 'https://www4-test.tomraconnect.com',
  'eu1.prod.service-portal.tomra.cloud': 'https://www1.tomraconnect.com',
  'eu2.prod.service-portal.tomra.cloud': 'https://www2.tomraconnect.com',
  'us.prod.service-portal.tomra.cloud': 'https://www3.tomraconnect.com',
  'au.prod.service-portal.tomra.cloud': 'https://www4.tomraconnect.com'
};

/**
 * Resolves the legacy Service Portal URL to embed, based on the hostname
 * the shell is currently being served from.
 *
 * Falls back to `VITE_LEGACY_PORTAL_URL` (set in `.env.example`/`.env.local`)
 * for local development, where `hostname` is typically `localhost`.
 *
 * Returns `undefined` if a production/test hostname is not recognized, so
 * callers can surface a clear error instead of silently embedding the
 * wrong environment.
 */
export function resolveLegacyPortalUrl(hostname: string): string | undefined {
  if (hostname in LEGACY_PORTAL_URL_BY_HOSTNAME) {
    return LEGACY_PORTAL_URL_BY_HOSTNAME[hostname];
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_LEGACY_PORTAL_URL;
  }

  return undefined;
}

/**
 * Adds `embedded=true` to a legacy portal URL so the legacy webapp renders
 * its chrome-less (compact) layout instead of its own header/menu/footer,
 * which this shell already provides around the iframe.
 *
 * The legacy app persists the flag in its session on first request, so it
 * only needs to be present on the initial iframe load.
 */
export function withEmbeddedFlag(portalUrl: string): string {
  const url = new URL(portalUrl);
  url.searchParams.set('embedded', 'true');
  return url.toString();
}

export const REFRESH_LOOP_MIN_LOADS = 5;
export const REFRESH_LOOP_MIN_INTERVAL_MS = 1000;
export const REFRESH_LOOP_MAX_INTERVAL_MS = 6000;
export const REFRESH_LOOP_MAX_INTERVAL_SPREAD_MS = 1500;

/**
 * Detects a misconfigured legacy portal that is self-reloading in a loop.
 *
 * We cannot read the cross-origin child URL, so every iframe `load` looks
 * identical whether it's a machine loop or a human clicking links. Raw load
 * count false-positives on normal browsing. The reliable signal is cadence
 * regularity: a broken error page reloads at a metronome-like, low-variance
 * interval, whereas human navigation is bursty and irregular. We therefore
 * require several consecutive loads whose intervals all sit within a "loop
 * band" and whose spread (max - min) is tight.
 *
 * Pure: timestamps (ascending, in ms) are passed in; no clock reads, no side
 * effects.
 */
export function isRefreshLoop(loadTimestampsMs: number[]): boolean {
  if (loadTimestampsMs.length < REFRESH_LOOP_MIN_LOADS) {
    return false;
  }

  const recent = loadTimestampsMs.slice(-REFRESH_LOOP_MIN_LOADS);
  const intervals: number[] = [];
  for (let i = 1; i < recent.length; i++) {
    intervals.push(recent[i] - recent[i - 1]);
  }

  const withinBand = intervals.every(
    (interval) =>
      interval >= REFRESH_LOOP_MIN_INTERVAL_MS && interval <= REFRESH_LOOP_MAX_INTERVAL_MS
  );
  if (!withinBand) {
    return false;
  }

  const spread = Math.max(...intervals) - Math.min(...intervals);
  return spread <= REFRESH_LOOP_MAX_INTERVAL_SPREAD_MS;
}
